import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Layers } from 'lucide-react';
import { PLATFORMS, ASSET_TYPES, supportsAssetType, aspectForAssetType } from '@/lib/clipPlatformFormats';

/**
 * Multi-platform clip variants (required Clips feature): one source range,
 * several platform-tagged clip records created in one action, each with
 * its own real, independently-approvable EditorClip id and its own
 * `target_aspect_ratio` (a real, persisted field) — reuses the exact same
 * `onGenerate` the manual/AI generation panel uses, once per platform.
 */
export function ClipVariantGenerator({ disabled, approvedDuration, onGenerateVariants, generating }) {
  const [assetType, setAssetType] = useState('short_form');
  const [platforms, setPlatforms] = useState(['youtube']);
  const [start, setStart] = useState(0);
  const [end, setEnd] = useState(Math.min(approvedDuration || 45, 45));

  const togglePlatform = (key) => {
    setPlatforms((ps) => (ps.includes(key) ? ps.filter((p) => p !== key) : [...ps, key]));
  };

  const validPlatforms = platforms.filter((p) => supportsAssetType(p, assetType));
  const invalidCount = platforms.length - validPlatforms.length;

  return (
    <div className="space-y-3 p-3 rounded-lg border border-white/10 bg-white/[0.02]" data-testid="clip-variant-generator">
      <p className="text-xs font-medium text-white flex items-center gap-1.5"><Layers className="h-3.5 w-3.5 text-[#3A7BFF]" />Multi-Platform Variants</p>
      <p className="text-[11px] text-[#8A93A6]">Pick one range, then every platform below becomes its own editable clip variant — approve one while you keep adjusting another.</p>

      <div>
        <label className="text-[11px] text-[#8A93A6] mb-1 block">Content type</label>
        <Select value={assetType} onValueChange={setAssetType}>
          <SelectTrigger className="h-8 text-xs bg-white/5 border-white/10" data-testid="variant-asset-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ASSET_TYPES.map((t) => <SelectItem key={t.key} value={t.key}>{t.label} ({t.aspect})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        <label className="text-[11px] text-[#8A93A6] mb-1 block">Destinations</label>
        <div className="grid grid-cols-2 gap-1.5">
          {PLATFORMS.map((p) => {
            const supported = supportsAssetType(p.key, assetType);
            return (
              <label
                key={p.key}
                className={`flex items-center gap-2 px-2 py-1.5 rounded-md border text-xs ${supported ? 'border-white/10 bg-white/[0.03] text-white cursor-pointer' : 'border-white/5 text-[#555E6E] cursor-not-allowed opacity-60'}`}
                data-testid={`variant-platform-option-${p.key}`}
              >
                <Checkbox
                  checked={platforms.includes(p.key)} disabled={!supported}
                  onCheckedChange={() => togglePlatform(p.key)}
                  data-testid={`variant-platform-checkbox-${p.key}`}
                />
                {p.label}{!supported && <span className="text-[9px] ml-auto">N/A</span>}
              </label>
            );
          })}
        </div>
        {invalidCount > 0 && (
          <p className="text-[10px] text-amber-400/80 mt-1">{invalidCount} selected destination{invalidCount > 1 ? 's don\'t' : ' doesn\'t'} support this content type and will be skipped.</p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[11px] text-[#8A93A6] mb-1 block">Start (s)</label>
          <Input type="number" step="0.1" value={start} onChange={(e) => setStart(Number(e.target.value))} className="h-8 text-xs bg-white/5 border-white/10" data-testid="variant-range-start-input" />
        </div>
        <div>
          <label className="text-[11px] text-[#8A93A6] mb-1 block">End (s)</label>
          <Input type="number" step="0.1" value={end} onChange={(e) => setEnd(Number(e.target.value))} className="h-8 text-xs bg-white/5 border-white/10" data-testid="variant-range-end-input" />
        </div>
      </div>

      {disabled && (
        <p className="text-[11px] text-amber-400/80">Approve the Master in Main Editor first — variants are generated from the latest approved version, never a draft.</p>
      )}

      <Button
        size="sm" className="w-full bg-[#3A7BFF] hover:bg-[#3A7BFF]/90"
        disabled={disabled || generating || validPlatforms.length === 0 || end <= start}
        onClick={() => onGenerateVariants({ assetType, platforms: validPlatforms, start, end, aspect: aspectForAssetType(assetType) })}
        data-testid="generate-variants-button"
      >
        {generating ? 'Generating…' : `Create ${validPlatforms.length || ''} Variant${validPlatforms.length === 1 ? '' : 's'}`}
      </Button>
      <p className="text-[10px] text-[#555E6E]">Platform duration limits aren't enforced here yet — double-check each platform's current requirements before publishing.</p>
    </div>
  );
}
